import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Plus, ArrowRightLeft, Package } from "lucide-react";
import {
  Page, Panel, SearchInput, usePagination, Pagination, DeleteButton,
  Modal, ModalActions, Field, SelectField, TextArea,
} from "../lib/ui";
import { api, humanDate, today } from "../lib/api";
import { CustomerCombobox, StockCombobox } from "./Modals";

function WarrantyModal({ customers, stocks, onClose, onSaved, onCustomerAdded }) {
  const [form, setForm] = useState({
    customer_id: null, customer_name: "", stock_id: null, stock_name: "",
    removed_serial_no: "", installed_serial_no: "",
    reason: "", qty: 1, date: today(),
    related_invoice_id: "", note: "",
  });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/warranty-records", form);
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Modal title="Yeni Garanti Değişimi" subtitle="Kayıt oluşunca ana stoktan düşülüp garanti deposuna eklenir."
           onClose={onClose} testid="warranty-modal" size="lg">
      <form onSubmit={submit} data-testid="warranty-form">
        <div className="row">
          <div style={{ flex: 1 }}>
            <label className="field-label">Müşteri</label>
            <CustomerCombobox customers={customers} selectedId={form.customer_id} selectedName={form.customer_name}
                              onSelect={(c) => setForm({ ...form, customer_id: c?.id || null, customer_name: c?.name || "" })}
                              onCreateNew={onCustomerAdded} />
          </div>
          <Field label="Tarih" testid="warranty-date" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="row">
          <div style={{ flex: 2 }}>
            <label className="field-label">Değişen Ürün (stoktan)</label>
            <StockCombobox stocks={stocks} selectedId={form.stock_id} selectedLabel={form.stock_name}
                           onSelect={(s) => setForm({ ...form, stock_id: s?.id || null, stock_name: s?.name || "" })}
                           index={0} />
          </div>
          <Field label="Adet" testid="warranty-qty" type="number" min="0.01" step="0.01" value={form.qty}
                 onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} />
        </div>
        <div className="row">
          <Field label="Sökülen (arızalı) ürün seri no" testid="warranty-removed-serial" value={form.removed_serial_no}
                 onChange={(e) => setForm({ ...form, removed_serial_no: e.target.value })} />
          <Field label="Takılan (yeni) ürün seri no" testid="warranty-installed-serial" value={form.installed_serial_no}
                 onChange={(e) => setForm({ ...form, installed_serial_no: e.target.value })} />
        </div>
        <div className="row">
          <Field label="İlgili Fatura/Fiş (opsiyonel)" testid="warranty-related" value={form.related_invoice_id}
                 onChange={(e) => setForm({ ...form, related_invoice_id: e.target.value })} placeholder="Fatura no veya fiş no" />
        </div>
        <TextArea label="Arıza / Değişim Sebebi" testid="warranty-reason" value={form.reason} rows={2}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        {err && <div className="error-text">{err}</div>}
        <ModalActions onCancel={onClose} submitLabel="Kaydet" submitTestId="warranty-submit" />
      </form>
    </Modal>
  );
}

function TransferModal({ stocks, onClose, onSaved }) {
  const [form, setForm] = useState({ stock_id: null, stock_name: "", qty: 1, direction: "to_main" });
  const [err, setErr] = useState("");
  const picked = stocks.find((s) => s.id === form.stock_id);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.stock_id) return setErr("Ürün seçin");
    try {
      await api.post("/warranty-transfer", { stock_id: form.stock_id, qty: form.qty, direction: form.direction });
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || "Transfer başarısız"); }
  };
  return (
    <Modal title="Depolar Arası Transfer" subtitle="Garanti ↔ Ana depo arasında ürün aktarın."
           onClose={onClose} testid="transfer-modal">
      <form onSubmit={submit} data-testid="warranty-transfer-form">
        <div style={{ marginBottom: 12 }}>
          <label className="field-label">Ürün</label>
          <StockCombobox stocks={stocks} selectedId={form.stock_id} selectedLabel={form.stock_name}
                         onSelect={(s) => setForm({ ...form, stock_id: s?.id || null, stock_name: s?.name || "" })}
                         index={0} />
        </div>
        {picked && (
          <div className="muted" style={{ marginBottom: 10, fontSize: 12 }} data-testid="transfer-stock-state">
            Ana depo: <b>{picked.quantity || 0}</b> · Garanti deposu: <b>{picked.warranty_quantity || 0}</b>
          </div>
        )}
        <div className="row">
          <SelectField label="Yön" testid="transfer-direction" value={form.direction}
                       onChange={(e) => setForm({ ...form, direction: e.target.value })}>
            <option value="to_warranty">Ana → Garanti</option>
            <option value="to_main">Garanti → Ana</option>
          </SelectField>
          <Field label="Adet" testid="transfer-qty" type="number" min="0.01" step="0.01" value={form.qty}
                 onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} />
        </div>
        {err && <div className="error-text">{err}</div>}
        <ModalActions onCancel={onClose} submitLabel="Aktar" submitTestId="transfer-submit" />
      </form>
    </Modal>
  );
}

export default function Warranty({ user, customers = [], stocks = [], onCustomerAdded, reload }) {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("records");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    const { data } = await api.get("/warranty-records");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const n = q.toLowerCase();
    return rows.filter((r) => [r.customer_name, r.stock_name, r.serial_no, r.reason]
      .some((v) => (v || "").toLowerCase().includes(n)));
  }, [rows, q]);
  const pg = usePagination(filtered);
  const warrantyStocks = useMemo(() => stocks.filter((s) => (s.warranty_quantity || 0) > 0), [stocks]);

  const del = async (id) => {
    if (!window.confirm("Garanti kaydı silinsin mi? (stok hareketi geri alınır)")) return;
    await api.delete(`/warranty-records/${id}`);
    load(); reload?.();
  };

  return (
    <>
      <Page eyebrow="MUHASEBE" title="Garanti Değişimleri"
            subtitle="Garantiden değişen ürünler burada tutulur. Ana stoktan düşüp garanti deposunda birikir."
            action={
              <div style={{ display: "flex", gap: 8 }}>
                <button className="icon-button" data-testid="warranty-transfer-btn"
                        onClick={() => setModal({ type: "transfer" })}>
                  <ArrowRightLeft size={14} /> Depo Transferi
                </button>
                <button className="primary-button" data-testid="warranty-add-btn"
                        onClick={() => setModal({ type: "new" })}>
                  <Plus size={14} /> Yeni Garanti Değişimi
                </button>
              </div>
            } />
      <Panel className="module-panel">
        <div className="tab-row" style={{ marginBottom: 10 }}>
          <button className={tab === "records" ? "tab active" : "tab"} data-testid="warranty-tab-records"
                  onClick={() => setTab("records")}>Değişim Kayıtları ({rows.length})</button>
          <button className={tab === "stock" ? "tab active" : "tab"} data-testid="warranty-tab-stock"
                  onClick={() => setTab("stock")}>Garanti Deposu ({warrantyStocks.length})</button>
        </div>
        {tab === "records" ? (
          <>
            <div style={{ marginBottom: 10 }}>
              <SearchInput value={q} onChange={setQ} testid="warranty-search"
                           placeholder="Müşteri, ürün, seri no, sebep ara" />
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Tarih</th><th>Müşteri</th><th>Ürün</th><th>Sökülen Seri No</th><th>Takılan Seri No</th>
                      <th className="amount">Adet</th><th>Sebep</th><th>İlgili</th><th></th></tr>
                </thead>
                <tbody>
                  {pg.slice.length === 0 && <tr><td colSpan="9" className="empty">Garanti kaydı yok.</td></tr>}
                  {pg.slice.map((r) => (
                    <tr key={r.id} data-testid={`warranty-row-${r.id}`}>
                      <td>{humanDate(r.date || r.created_at)}</td>
                      <td><strong>{r.customer_name || "—"}</strong></td>
                      <td><ShieldCheck size={12} /> {r.stock_name || "—"}</td>
                      <td>{r.removed_serial_no || r.serial_no || "—"}</td>
                      <td>{r.installed_serial_no || "—"}</td>
                      <td className="amount">{r.qty}</td>
                      <td className="truncate" title={r.reason}>{r.reason || "—"}</td>
                      <td>{r.related_invoice_id || "—"}</td>
                      <td className="row-actions">
                        {user.role === "admin" && (
                          <DeleteButton testid={`warranty-delete-${r.id}`} onClick={() => del(r.id)} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                          onChange={pg.setPage} testid="warranty-pagination" />
            </div>
          </>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Kod</th><th>Ürün</th><th className="amount">Ana Depo</th>
                    <th className="amount">Garanti Deposu</th></tr>
              </thead>
              <tbody>
                {warrantyStocks.length === 0 && <tr><td colSpan="4" className="empty">Garanti deposunda ürün yok.</td></tr>}
                {warrantyStocks.map((s) => (
                  <tr key={s.id} data-testid={`warranty-stock-${s.id}`}>
                    <td>{s.code}</td>
                    <td><Package size={12} /> {s.name}</td>
                    <td className="amount">{s.quantity || 0}</td>
                    <td className="amount"><strong>{s.warranty_quantity || 0}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {modal?.type === "new" && (
        <WarrantyModal customers={customers} stocks={stocks} onClose={() => setModal(null)}
                       onSaved={() => { setModal(null); load(); reload?.(); }}
                       onCustomerAdded={onCustomerAdded} />
      )}
      {modal?.type === "transfer" && (
        <TransferModal stocks={stocks} onClose={() => setModal(null)}
                       onSaved={() => { setModal(null); reload?.(); }} />
      )}
    </>
  );
}
